import React, { useMemo } from 'react'
import NavBarItem from './nav_bar_item'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { useAppSelector } from '@verzekeringapp-mono/shared/src/hooks/state'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import ClaimDamageButton from '~/components/ClaimDamageButton'
import { Link } from 'react-router-dom'
import { agencyOpenState, renderOpeningHoursText } from '@verzekeringapp-mono/shared/src/utils/agency_utils'
import { selectEffectiveTogglesForCurrent } from '@verzekeringapp-mono/shared/src/state/selectors/resource_authorization'
import { Images } from '@verzekeringapp-mono/shared/src/assets'

interface NavBarButtonItem {
  href: string
  icon: IconProp
  title: string
}

export const getButtons = (showMemberBenefits: boolean): NavBarButtonItem[] => {
  const buttons: NavBarButtonItem[] = showMemberBenefits
    ? [
      {
        href: '/overzicht',
        icon: 'home',
        title: 'Overzicht'
      },
      {
        href: '/dossier',
        icon: 'folder',
        title: 'Mijn dossier'
      },
      {
        href: '/verzekeringen',
        icon: 'copy',
        title: 'Mijn verzekeringen'
      },
      {
        href: '/adviseur',
        icon: 'user-tie',
        title: 'Adviseur'
      },
      {
        href: '/ledenvoordelen',
        icon: 'tag',
        title: 'Ledenvoordelen'
      },
      {
        href: '/meer',
        icon: 'ellipsis-v',
        title: 'Meer'
      }
    ]
    : [
      {
        href: '/overzicht',
        icon: 'home',
        title: 'Overzicht'
      },
      {
        href: '/dossier',
        icon: 'folder',
        title: 'Mijn dossier'
      },
      {
        href: '/verzekeringen',
        icon: 'copy',
        title: 'Mijn verzekeringen'
      },
      {
        href: '/adviseur',
        icon: 'user-tie',
        title: 'Adviseur'
      },
      {
        href: '/meer',
        icon: 'ellipsis-v',
        title: 'Meer'
      }
    ]

  return buttons
}

const NavBar = () => {
  const relationToggles = useAppSelector(selectEffectiveTogglesForCurrent)

  const agency = useAppSelector(state => state.agency.agency)
  const agencyOpenStateValue = useMemo(() => agencyOpenState(agency?.openingHours), [agency])

  return (
    <div className='h-full overflow-y-auto bg-background-chrome md:w-[250px] lg:w-[330px]'>
      <nav aria-label='navigatie'>
        <ul className='flex flex-col gap-2 list-none p-0 m-0'>
          {getButtons(relationToggles?.cooperationMembership ?? false).map((item) => (
            <li key={`nav-bar-item-${item.href}`}>
              <NavBarItem
                href={item.href}
                icon={item.icon}
                title={item.title}
              />
            </li>
          ))}
        </ul>
      </nav>

      <div className='flex justify-center px-8'>
        <ClaimDamageButton />
      </div>

      <div className='flex flex-col gap-8 border-y-[0.5px] border-border-subtle py-8 px-8'>
        <Link
          to='/adviseur'
          className='md:block hidden'
        >
          <div className='h-[80px] w-[80px] p-2 rounded-full border border-border-default bg-surface-logo-backdrop'>
            <img
              src={agency?.logoUrl ?? Images.Logo}
              width={127}
              className='h-full object-contain'
              alt='Logo van jouw verzekeringsadviseur'
            />
          </div>
        </Link>

        <p className='light'>
          {agency?.name ?? 'NH1816 Verzekeringen'}
        </p>

        {agency && (
          <div className='flex flex-col gap-4'>
            <div className='flex items-center gap-2'>
              {
                agencyOpenStateValue !== 'unknown' && (
                  <div
                    className={`mr-2 h-[8px] w-[8px] rounded-full ${
                      agencyOpenStateValue === 'open'
                        ? 'bg-status-success-foreground'
                        : 'bg-status-danger-foreground'
                    }`}
                  />
                )
              }

              {agency?.openingHours && agencyOpenStateValue !== 'unknown' && (
                <p className='text-sm'>
                  {renderOpeningHoursText(agency.openingHours)}
                </p>
              )}

              {
                agencyOpenStateValue === 'unknown' && (
                  <p className='text-sm'>
                    Openingstijden onbekend
                  </p>
                )
              }
            </div>

            <div className='flex'>
              <FontAwesomeIcon
                icon='phone'
                size='lg'
                className='text-icon-brand'
              />

              {agency?.phoneNumber && (
                <a
                  href={`tel:${agency.phoneNumber}`}
                  className='ml-2 text-sm'
                >
                  {agency.phoneNumber}
                </a>
              )}
            </div>

            <div className='flex'>
              <FontAwesomeIcon
                icon='envelope'
                size='lg'
                className='text-icon-brand'
              />

              {agency?.email && (
                <a
                  href={`mailto:${agency.email}`}
                  className='ml-2 text-sm'
                >
                  {agency.email}
                </a>
              )}
            </div>

            <Link
              className='link text-sm'
              to='/adviseur'
            >
              <FontAwesomeIcon
                icon={['fal', 'arrow-right']}
                className='text-icon-brand'
              />{' '}
              Bekijk het volledige profiel
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}

export default NavBar
